import { useState } from 'react'
import { signInWithCustomToken } from 'firebase/auth'
import { auth , isPlausiblePhone} from '@bas/shared'

/**
 * ClientLogin — SMS-code sign-in for the Control Center (migration Phase 1 step 4).
 *
 * The platform's first real credentialed client login. Rendered by App.tsx's
 * unauth branch (chrome-less), so it works at the SPA root on app.justtextava.com
 * AND under /control-center on the Firebase build. Flow: phone → texted 6-digit
 * code → verify → signInWithCustomToken → onAuthStateChanged re-renders App into
 * the authed dashboard.
 *
 * Branding (2026-07-17): this is the front door on app.justtextava.com, so it
 * wears the JustTextAva look — light teal surface, Ava avatar + wordmark, the
 * site's sticky header — to feel continuous with justtextava.com. It renders its
 * own self-contained light theme (scoped under .jta-login), independent of the
 * Control Center's dark app shell. Palette + wordmark mirror prototypes/justtextava.html.
 *
 * Backend: POST /api/auth/request-code + /api/auth/verify-code (auth/smsLogin.ts),
 * reached via the same relative /api path every SPA fetch uses.
 */

const AVA_IMG = `${import.meta.env.BASE_URL}Ava2.png`

type Step = 'phone' | 'code'

export function ClientLogin() {
  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  async function requestCode(e?: React.FormEvent) {
    e?.preventDefault()
    if (busy) return
    const trimmed = phone.trim()
    if (!trimmed) { setError('Enter your mobile number.'); return }
    // A typo here is a silent lock-out: the code goes to a number that is not
    // theirs (or nowhere), and the screen says "check your phone". Catch the
    // shape before we spend an SMS on it.
    if (!isPlausiblePhone(trimmed)) {
      setError('That number does not look complete — please check it.')
      return
    }
    setBusy(true); setError(null); setNotice(null)
    try {
      const res = await fetch('/api/auth/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: trimmed }),
      })
      // The endpoint is intentionally neutral (always ok) so it never reveals
      // whether a number is registered — advance to the code step regardless.
      if (!res.ok) throw new Error('request failed')
      setStep('code')
      setNotice('If that number is on file, we just texted you a 6-digit code.')
    } catch {
      setError('Could not send a code. Please check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  async function verifyCode(e?: React.FormEvent) {
    e?.preventDefault()
    if (busy) return
    const c = code.trim()
    if (!/^\d{6}$/.test(c)) { setError('Enter the 6-digit code.'); return }
    setBusy(true); setError(null); setNotice(null)
    try {
      const res = await fetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), code: c }),
      })
      const data = (await res.json().catch(() => null)) as
        | { ok?: boolean; token?: string; error?: string; remainingAttempts?: number }
        | null
      if (!res.ok || !data?.ok || !data.token) {
        const remain = data?.remainingAttempts
        setError(
          (data?.error || 'That code is not valid.') +
            (typeof remain === 'number' && remain > 0 ? ` ${remain} attempt${remain === 1 ? '' : 's'} left.` : ''),
        )
        return
      }
      // Establishes a same-origin Firebase session; onAuthStateChanged in useAuth
      // re-renders App into the authed shell. Keep the busy state on — the parent
      // swaps this component out on success.
      await signInWithCustomToken(auth, data.token)
      setNotice('Signing you in…')
    } catch {
      setError('Something went wrong. Please try again.')
      setBusy(false)
    }
  }

  return (
    <div className="jta-login">
      <style>{JTA_LOGIN_CSS}</style>

      <header className="jta-login-header">
        <div className="jta-login-headinner">
          <a className="jta-login-brand" href="https://justtextava.com" aria-label="JustTextAva home">
            <span className="jta-login-dot" style={{ backgroundImage: `url(${AVA_IMG})` }} />
            <span className="jta-login-word">Just<span className="jta-hl">Text</span>Ava</span>
          </a>
        </div>
      </header>

      <main className="jta-login-main">
        <div className="jta-login-card">
          <img src={AVA_IMG} alt="Ava" className="jta-login-hero" />
          <h1 className="jta-login-title">Sign in to your Control Center</h1>
          <p className="jta-login-sub">
            {step === 'phone'
              ? 'We’ll text a one-time code to your mobile number.'
              : 'Enter the 6-digit code we just texted you.'}
          </p>

          {step === 'phone' ? (
            <form onSubmit={requestCode} className="jta-login-form">
              <label className="jta-login-label" htmlFor="login-phone">Mobile number</label>
              <input
                id="login-phone"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                autoFocus
                className="jta-login-input"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <button type="submit" className="jta-login-btn" disabled={busy}>
                {busy ? 'Sending…' : 'Text me a code'}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="jta-login-form">
              <label className="jta-login-label" htmlFor="login-code">6-digit code</label>
              <input
                id="login-code"
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                className="jta-login-input jta-login-code"
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <button type="submit" className="jta-login-btn" disabled={busy}>
                {busy ? 'Verifying…' : 'Sign in'}
              </button>
              <div className="jta-login-links">
                <button
                  type="button"
                  className="jta-login-link"
                  disabled={busy}
                  onClick={() => { setStep('phone'); setCode(''); setError(null); setNotice(null) }}
                >
                  Use a different number
                </button>
                <button type="button" className="jta-login-link" disabled={busy} onClick={() => requestCode()}>
                  Resend code
                </button>
              </div>
            </form>
          )}

          {error && <div className="jta-login-error">{error}</div>}
          {notice && !error && <p className="jta-login-notice">{notice}</p>}
        </div>

        <p className="jta-login-foot">
          justtextava.com is a product of{' '}
          <a href="https://bestaisolutions.com" target="_blank" rel="noopener">Best AI Solutions</a>.
        </p>
      </main>
    </div>
  )
}

/* Self-contained JustTextAva palette — mirrors prototypes/justtextava.html so the
   login reads as a continuation of the marketing site, independent of the app's
   dark theme. Scoped under .jta-login so it never touches the authed shell. */
const JTA_LOGIN_CSS = `
.jta-login{
  --ink:#0f172a; --ink-soft:#475569; --muted:#94a3b8; --line:#e2e8f0;
  --bg:#f8fafc; --card:#ffffff; --accent:#0d9488; --accent-ink:#0f766e;
  --accent-soft:#ccfbf1; --accent-glow:rgba(13,148,136,.30);
  position:fixed; inset:0; z-index:100; overflow-y:auto;
  background:var(--bg); color:var(--ink);
  font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
  -webkit-font-smoothing:antialiased; line-height:1.5;
}
.jta-login-header{
  position:sticky; top:0; z-index:2;
  background:rgba(248,250,252,.88); backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
  border-bottom:1px solid var(--line);
}
.jta-login-headinner{ max-width:1120px; margin:0 auto; padding:14px 24px; }
.jta-login-brand{
  display:inline-flex; align-items:center; gap:10px;
  text-decoration:none; color:inherit; transition:opacity .12s ease;
}
.jta-login-brand:hover{ opacity:.8; }
.jta-login-dot{
  width:34px; height:34px; border-radius:50%;
  background-size:cover; background-position:center; background-repeat:no-repeat;
  border:1px solid var(--line); flex:none;
}
.jta-login-word{ font-weight:800; font-size:19px; letter-spacing:-.3px; color:var(--ink); }
.jta-hl{ color:var(--accent); }
.jta-login-main{
  min-height:calc(100% - 63px);
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:40px 20px 56px;
}
.jta-login-card{
  width:100%; max-width:400px; background:var(--card);
  border:1px solid var(--line); border-radius:20px; padding:36px 32px 32px;
  box-shadow:0 18px 50px rgba(15,23,42,.10); text-align:center;
}
.jta-login-hero{
  width:96px; height:96px; border-radius:50%; object-fit:cover;
  margin:0 auto 18px; display:block; border:3px solid #fff;
  box-shadow:0 0 0 2px var(--accent-soft), 0 8px 22px rgba(13,148,136,.18);
}
.jta-login-title{ font-size:22px; font-weight:800; letter-spacing:-.4px; margin:0 0 6px; color:var(--ink); }
.jta-login-sub{ font-size:14.5px; color:var(--ink-soft); margin:0 auto; max-width:26em; }
.jta-login-form{ margin-top:26px; text-align:left; }
.jta-login-label{ display:block; font-size:13px; font-weight:600; color:var(--ink-soft); margin:0 0 7px 2px; }
.jta-login-input{
  width:100%; box-sizing:border-box; padding:14px 16px;
  border:1px solid var(--line); border-radius:12px; font-size:16px; font-family:inherit;
  background:#fff; color:var(--ink); outline:none; transition:border-color .15s ease, box-shadow .15s ease;
}
.jta-login-input::placeholder{ color:var(--muted); }
.jta-login-input:focus{ border-color:var(--accent); box-shadow:0 0 0 3px rgba(13,148,136,.20); }
.jta-login-code{ text-align:center; letter-spacing:.42em; font-size:20px; font-weight:600; }
.jta-login-btn{
  width:100%; margin-top:16px; cursor:pointer; border:none; font-family:inherit;
  font-weight:700; font-size:16px; color:#fff; background:var(--accent);
  padding:15px 20px; border-radius:999px; box-shadow:0 10px 24px var(--accent-glow);
  transition:transform .12s ease, box-shadow .12s ease, background .15s ease;
}
.jta-login-btn:hover:not(:disabled){ box-shadow:0 14px 30px rgba(13,148,136,.42); transform:translateY(-1px); }
.jta-login-btn:disabled{ opacity:.6; cursor:default; }
.jta-login-links{ display:flex; align-items:center; justify-content:space-between; margin-top:16px; }
.jta-login-link{
  background:none; border:none; padding:0; cursor:pointer; font-family:inherit;
  font-size:13px; font-weight:600; color:var(--accent-ink); transition:color .12s ease;
}
.jta-login-link:hover:not(:disabled){ color:var(--accent); text-decoration:underline; }
.jta-login-link:disabled{ opacity:.5; cursor:default; }
.jta-login-error{
  margin-top:18px; padding:11px 14px; border-radius:12px; font-size:13px; text-align:left;
  color:#b91c1c; background:#fef2f2; border:1px solid #fecaca;
}
.jta-login-notice{ margin-top:18px; font-size:13px; color:var(--ink-soft); text-align:center; }
.jta-login-foot{ margin-top:24px; font-size:12.5px; color:#64748b; text-align:center; }
.jta-login-foot a{ color:var(--accent-ink); text-decoration:underline; text-underline-offset:2px; }
.jta-login-foot a:hover{ color:var(--accent-ink); }
`
